import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Filter, 
  RotateCcw, 
  Eye, 
  Clock, 
  User, 
  FileText, 
  Calendar,
  Layers
} from 'lucide-react';
import { SystemLogItem, SystemLogFilter } from '../../pages/settings/systemLogs.types';
import { SystemLogsController } from '../../pages/settings/systemLogs.controller';
import { UsersController } from '../../pages/settings/users.controller';
import { UserItem } from '../../pages/settings/users.types';

interface SystemLogViewProps {
  onBack: () => void;
}

export default function SystemLogView({ onBack }: SystemLogViewProps) {
  const [logs, setLogs] = useState<SystemLogItem[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtros ativos
  const [filters, setFilters] = useState<SystemLogFilter>({
    userId: null,
    resource: 'Todos',
    startDate: '',
    endDate: ''
  });

  // Modal para exibir detalhes adicionais
  const [selectedLog, setSelectedLog] = useState<SystemLogItem | null>(null);

  // Carregar dados iniciais (Usuários e logs padrão)
  useEffect(() => {
    async function init() {
      setIsLoading(true);
      setError(null);
      try {
        // Obter lista de usuários
        const uList = await UsersController.listFromServer();
        setUsers(uList);

        // Obter logs com filtros atuais
        const fetched = await SystemLogsController.fetchLogs(filters);
        setLogs(fetched);
      } catch (err: any) {
        setError('Não foi possível carregar os dados de auditoria.');
      } finally {
        setIsLoading(false);
      }
    }
    init();
  }, []);

  const handleFilterChange = (field: keyof SystemLogFilter, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilter = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const fetched = await SystemLogsController.fetchLogs(filters);
      setLogs(fetched);
    } catch (err) {
      setError('Erro ao filtrar a trilha de auditoria.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = async () => {
    const cleared: SystemLogFilter = {
      userId: null,
      resource: 'Todos',
      startDate: '',
      endDate: ''
    };
    setFilters(cleared);
    setIsLoading(true);
    setError(null);
    try {
      const fetched = await SystemLogsController.fetchLogs(cleared);
      setLogs(fetched);
    } catch (err) {
      setError('Erro ao recomeçar a listagem.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDetails = (detailsStr: string) => {
    if (!detailsStr) return 'Nenhum detalhe adicional registrado.';
    try {
      const parsed = JSON.parse(detailsStr);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return detailsStr;
    }
  };

  const formatEventDate = (dateStr: string) => {
    if (!dateStr) return 'Sem data';
    try {
      const normalized = dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr;
      const parsed = new Date(normalized);
      if (isNaN(parsed.getTime())) {
        return dateStr;
      }
      return parsed.toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="animate-fade-in py-1" id="system-logs-viewport">
      {/* Cabeçalho */}
      <div className="border-bottom pb-3 mb-4 d-flex align-items-center justify-content-between">
        <div>
          <h1 className="h4 fw-black text-dark font-display mb-1 d-flex align-items-center gap-2">
            <Layers size={22} className="text-secondary" />
            Trilha de Auditoria (System Logs)
          </h1>
          <p className="text-muted text-xs font-mono mb-0">
            Monitoramento de operações críticas realizadas por administradores e operadores.
          </p>
        </div>
        <button 
          onClick={onBack}
          className="btn btn-outline-secondary btn-xs d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-3 border-secondary-subtle"
          id="btn-back-dashboard"
        >
          <ArrowLeft size={14} />
          <span>Voltar</span>
        </button>
      </div>

      {/* Erro */}
      {error && (
        <div className="alert alert-danger border-0 rounded-3 shadow-xs mb-4" role="alert" id="logs-error-alert">
          {error}
        </div>
      )}

      {/* Bloco de Filtros */}
      <div className="card border rounded-3 shadow-xs mb-4 bg-white" id="logs-filter-card">
        <div className="card-header bg-light border-bottom py-2.5 px-3">
          <span className="text-xs fw-bold text-dark font-sans d-flex align-items-center gap-1.5">
            <Filter size={14} className="text-muted" />
            Filtros Avançados
          </span>
        </div>
        <div className="card-body p-3">
          <form onSubmit={handleApplyFilter} className="row g-3" id="logs-filter-form">
            {/* Usuário */}
            <div className="col-12 col-md-3">
              <label className="form-label text-xxs text-secondary fw-semibold font-mono mb-1">USUÁRIO</label>
              <select 
                className="form-select form-select-sm text-xs rounded-3"
                value={filters.userId || ''}
                onChange={(e) => handleFilterChange('userId', e.target.value ? parseInt(e.target.value) : null)}
                id="select-filter-user"
              >
                <option value="">Todos os Usuários</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.fullName} ({u.sector})</option>
                ))}
              </select>
            </div>

            {/* Módulo / Recurso */}
            <div className="col-12 col-md-3">
              <label className="form-label text-xxs text-secondary fw-semibold font-mono mb-1">MÓDULO / RECURSO</label>
              <select 
                className="form-select form-select-sm text-xs rounded-3"
                value={filters.resource}
                onChange={(e) => handleFilterChange('resource', e.target.value)}
                id="select-filter-resource"
              >
                <option value="Todos">Todos os Módulos</option>
                <option value="Pedidos">Pedidos</option>
                <option value="Produtos">Produtos</option>
                <option value="Categorias">Categorias</option>
                <option value="Status">Status</option>
                <option value="Configuracao">Configuração</option>
              </select>
            </div>

            {/* Data Inicial */}
            <div className="col-12 col-md-2">
              <label className="form-label text-xxs text-secondary fw-semibold font-mono mb-1">DATA INICIAL</label>
              <input 
                type="date"
                className="form-control form-control-sm text-xs rounded-3"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                id="input-filter-start-date"
              />
            </div>

            {/* Data Final */}
            <div className="col-12 col-md-2">
              <label className="form-label text-xxs text-secondary fw-semibold font-mono mb-1">DATA FINAL</label>
              <input 
                type="date"
                className="form-control form-control-sm text-xs rounded-3"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                id="input-filter-end-date"
              />
            </div>

            {/* Ações */}
            <div className="col-12 col-md-2 d-flex align-items-end gap-2">
              <button 
                type="button"
                onClick={handleClearFilters}
                className="btn btn-light btn-sm text-xs rounded-3 w-50 border py-2 d-flex align-items-center justify-content-center gap-1"
                title="Limpar filtros"
                id="btn-clear-logs-filter"
              >
                <RotateCcw size={13} />
                <span>Limpar</span>
              </button>
              <button 
                type="submit"
                className="btn btn-secondary btn-sm text-xs rounded-3 w-50 py-2 d-flex align-items-center justify-content-center gap-1"
                id="btn-apply-logs-filter"
              >
                <Filter size={13} />
                <span>Filtrar</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Histórico / Tabela de Logs */}
      <div className="card border rounded-3 shadow-xs bg-white mb-4" id="logs-table-card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" id="logs-data-table">
              <thead className="table-light">
                <tr className="border-bottom text-xxs text-secondary font-mono">
                  <th className="py-2.5 px-3" style={{ width: '180px' }}>DATA / HORA</th>
                  <th className="py-2.5 px-3">USUÁRIO</th>
                  <th className="py-2.5 px-3" style={{ width: '140px' }}>AÇÃO</th>
                  <th className="py-2.5 px-3" style={{ width: '160px' }}>MÓDULO</th>
                  <th className="py-2.5 px-3 text-end" style={{ width: '120px' }}>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted font-mono text-xs">
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Carregando registros da trilha...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted font-sans text-xs">
                      Nenhum registro de auditoria encontrado para os filtros selecionados.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    // Mapeamento de badges de cor para as ações
                    let actionBadgeColor = 'bg-secondary-subtle text-secondary-emphasis';
                    if (log.action === 'CRIAR') actionBadgeColor = 'bg-success-subtle text-success-emphasis';
                    if (log.action === 'ATUALIZAR') actionBadgeColor = 'bg-primary-subtle text-primary-emphasis';
                    if (log.action === 'EXCLUIR') actionBadgeColor = 'bg-danger-subtle text-danger-emphasis';

                    return (
                      <tr key={log.id} className="border-bottom hover-bg-light transition-all text-xs" id={`log-row-${log.id}`}>
                        {/* Data / Hora */}
                        <td className="py-2.5 px-3 font-mono text-muted d-flex align-items-center gap-1.5">
                          <Clock size={12} />
                          {formatEventDate(log.createdAt)}
                        </td>

                        {/* Usuário */}
                        <td className="py-2.5 px-3">
                          <div className="d-flex align-items-center gap-1.5">
                            <User size={12} className="text-secondary" />
                            <div>
                              <div className="fw-semibold text-dark">{log.userName}</div>
                              <div className="text-muted text-xxs font-mono">{log.userSector}</div>
                            </div>
                          </div>
                        </td>

                        {/* Ação */}
                        <td className="py-2.5 px-3">
                          <span className={`badge ${actionBadgeColor} fw-semibold text-xxs font-mono px-2 py-1 rounded`}>
                            {log.action}
                          </span>
                        </td>

                        {/* Módulo */}
                        <td className="py-2.5 px-3 font-mono text-muted">
                          {log.resource} {log.resourceId ? `#${log.resourceId}` : ''}
                        </td>

                        {/* Detalhes */}
                        <td className="py-2.5 px-3 text-end">
                          <button 
                            type="button"
                            onClick={() => setSelectedLog(log)}
                            className="btn btn-outline-secondary btn-xs d-inline-flex align-items-center gap-1 px-2.5 py-1 rounded"
                            title="Visualizar detalhes do log"
                            id={`btn-view-log-${log.id}`}
                          >
                            <Eye size={12} />
                            <span>Detalhes</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Detalhes Adicionais (Bootstrap 5 Puro/Simulado ou acoplado à página) */}
      {selectedLog && (
        <div 
          className="modal fade show d-block" 
          tabIndex={-1} 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          id="modal-log-details"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 rounded-4 shadow-lg animate-fade-in">
              <div className="modal-header bg-light border-bottom py-3 px-4 rounded-top-4">
                <h5 className="modal-title h6 fw-black text-dark font-display d-flex align-items-center gap-2 m-0">
                  <FileText size={18} className="text-secondary" />
                  Detalhes do Registro de Log #{selectedLog.id}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedLog(null)}
                  aria-label="Close"
                  id="btn-close-log-modal-header"
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-12 col-sm-6">
                    <span className="text-xxs text-secondary d-block font-mono mb-1">OPERADOR RESPONSÁVEL</span>
                    <strong className="text-xs text-dark">{selectedLog.userName} ({selectedLog.userSector})</strong>
                  </div>
                  <div className="col-12 col-sm-6">
                    <span className="text-xxs text-secondary d-block font-mono mb-1">DATA E HORA DO EVENTO</span>
                    <strong className="text-xs text-dark d-flex align-items-center gap-1.5">
                      <Clock size={13} />
                      {formatEventDate(selectedLog.createdAt)}
                    </strong>
                  </div>
                  <div className="col-12 col-sm-6">
                    <span className="text-xxs text-secondary d-block font-mono mb-1">AÇÃO EXECUTADA</span>
                    <span className="badge bg-secondary-subtle text-secondary-emphasis fw-semibold font-mono text-xxs px-2.5 py-1 mt-0.5">
                      {selectedLog.action}
                    </span>
                  </div>
                  <div className="col-12 col-sm-6">
                    <span className="text-xxs text-secondary d-block font-mono mb-1">RECURSO AFETADO</span>
                    <strong className="text-xs text-dark font-mono">
                      {selectedLog.resource} {selectedLog.resourceId ? `#${selectedLog.resourceId}` : ''}
                    </strong>
                  </div>
                </div>

                <div>
                  <span className="text-xxs text-secondary d-block font-mono mb-1.5">METADADOS E PAYLOAD DETALHADO (JSON)</span>
                  <div className="bg-light p-3 rounded-3 border">
                    <pre className="m-0 font-mono text-xxs text-dark overflow-auto" style={{ maxHeight: '250px' }}>
                      {formatDetails(selectedLog.details)}
                    </pre>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top bg-light p-3 rounded-bottom-4 justify-content-end">
                <button 
                  type="button" 
                  className="btn btn-secondary btn-sm px-4 rounded-3 text-xs"
                  onClick={() => setSelectedLog(null)}
                  id="btn-close-log-modal-footer"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
